import { theme } from './theme';
import type { BrandConfig } from '$lib/brand/types';

export class ThemeManager {
    private brandConfig: BrandConfig | null = null;
    private mediaQueryListener: (() => void) | null = null;

    constructor(brandConfig?: BrandConfig) {
        this.brandConfig = brandConfig || null;
        this.initializeTheme();
    }

    private initializeTheme(): void {
        // Subscribe to theme changes
        theme.subscribe(newTheme => {
            this.applyTheme(newTheme);
        });

        // Listen for system theme changes when in auto mode
        this.setupMediaQueryListener();
    }

    private setupMediaQueryListener(): void {
        // Skip if matchMedia is not available (e.g., in test environments)
        if (typeof globalThis.window === 'undefined' || !globalThis.window.matchMedia) {
            return;
        }

        // Remove existing listener if any
        if (this.mediaQueryListener) {
            globalThis.window
                .matchMedia('(prefers-color-scheme: dark)')
                .removeEventListener('change', this.mediaQueryListener);
        }

        // Add new listener
        this.mediaQueryListener = (): void => {
            // Reapply auto theme when system preference changes
            this.applyTheme('auto');
        };

        globalThis.window
            .matchMedia('(prefers-color-scheme: dark)')
            .addEventListener('change', this.mediaQueryListener);
    }

    private applyTheme(themeValue: string): void {
        const root = globalThis.document.documentElement;

        // Remove existing theme attributes
        root.removeAttribute('data-theme');

        // Apply new theme
        if (themeValue !== 'auto') {
            root.setAttribute('data-theme', themeValue);
        }

        // Apply brand-specific theme colors if available
        if (this.brandConfig?.theme) {
            this.applyBrandTheme(themeValue);
        }
    }

    private applyBrandTheme(themeValue: string): void {
        if (!this.brandConfig?.theme) return;

        const root = globalThis.document.documentElement;
        const themeSettings = this.brandConfig.theme;

        // Determine which theme to apply
        let themeToApply = null;
        if (themeValue === 'light' && themeSettings.lightTheme) {
            themeToApply = themeSettings.lightTheme;
        } else if (themeValue === 'dark' && themeSettings.darkTheme) {
            themeToApply = themeSettings.darkTheme;
        } else if (themeValue === 'auto') {
            // For auto theme, use system preference if available, otherwise default to light
            if (typeof globalThis.window !== 'undefined' && globalThis.window.matchMedia) {
                const prefersDark = globalThis.window.matchMedia(
                    '(prefers-color-scheme: dark)'
                ).matches;
                themeToApply = prefersDark ? themeSettings.darkTheme : themeSettings.lightTheme;
            } else {
                // Default to light theme in test environments
                themeToApply = themeSettings.lightTheme;
            }
        }

        if (themeToApply) {
            // Apply comprehensive theme colors
            root.style.setProperty('--color--primary', themeToApply.primary);
            root.style.setProperty('--color--secondary', themeToApply.secondary);
            root.style.setProperty('--color--accent', themeToApply.accent);
            root.style.setProperty('--color--text', themeToApply.text);
            root.style.setProperty('--color--text-secondary', themeToApply.textSecondary);
            root.style.setProperty('--color--page-background', themeToApply.background);
            root.style.setProperty(
                '--color--background-secondary',
                themeToApply.backgroundSecondary
            );

            // Also override the brand colors used in the layout
            root.style.setProperty('--brand-primary', themeToApply.primary);
            root.style.setProperty('--brand-secondary', themeToApply.secondary);
        }
    }

    public updateBrandConfig(brandConfig: BrandConfig): void {
        this.brandConfig = brandConfig;
        // Re-setup media query listener with new config
        this.setupMediaQueryListener();
        // The theme will be reapplied when the store subscription fires
    }

    public destroy(): void {
        // Clean up media query listener
        if (
            this.mediaQueryListener &&
            typeof globalThis.window !== 'undefined' &&
            globalThis.window.matchMedia
        ) {
            globalThis.window
                .matchMedia('(prefers-color-scheme: dark)')
                .removeEventListener('change', this.mediaQueryListener);
            this.mediaQueryListener = null;
        }
    }
}

// Global theme manager instance
let themeManager: ThemeManager | null = null;

export function initializeThemeManager(brandConfig?: BrandConfig): ThemeManager {
    if (!themeManager) {
        themeManager = new ThemeManager(brandConfig);
    } else if (brandConfig) {
        themeManager.updateBrandConfig(brandConfig);
    }
    return themeManager;
}

export function getThemeManager(): ThemeManager | null {
    return themeManager;
}
